import { useCallback, useMemo } from 'react';
import { DiaryReminderDto, ReminderChannel } from "../../models/diaryTypes";

// Helper function to check if two reminders are duplicates
export const areRemindersEqual = (r1: DiaryReminderDto, r2: DiaryReminderDto): boolean => {
    return (
        r1.offsetMinutes === r2.offsetMinutes &&
        r1.channel === r2.channel &&
        r1.targetUserId === r2.targetUserId &&
        r1.isEnabled === r2.isEnabled
    );
};

// Function to find duplicate reminders in an array
export const findDuplicateReminders = (reminders: DiaryReminderDto[]): number[] => {
    const duplicates: number[] = [];

    reminders.forEach((reminder, index) => {
        // Compare with all other reminders
        for (let i = index + 1; i < reminders.length; i++) {
            if (areRemindersEqual(reminder, reminders[i])) {
                if (!duplicates.includes(index)) duplicates.push(index);
                if (!duplicates.includes(i)) duplicates.push(i);
            }
        }
    });

    return duplicates;
};

// Custom hook for reminder validation
export const useReminderValidation = (reminders: DiaryReminderDto[]) => {
    const validationErrors = useMemo(() => {
        const errors: { [key: number]: string[] } = {};
        const seen = new Set<string>();

        reminders.forEach((reminder, index) => {
            const reminderKey = getReminderKey(reminder);

            if (seen.has(reminderKey)) {
                errors[index] = [...(errors[index] || []), 'Duplicate reminder'];
            } else {
                seen.add(reminderKey);
            }

            // You can add other validations here
            if (reminder.offsetMinutes === 0) {
                errors[index] = [...(errors[index] || []), 'Reminder cannot be set at exactly event time'];
            }
        });

        return errors;
    }, [reminders]);

    const hasDuplicates = useMemo(() => {
        const uniqueKeys = new Set(reminders.map(getReminderKey));
        return uniqueKeys.size !== reminders.length;
    }, [reminders]);

    const getUniqueReminders = useCallback((): DiaryReminderDto[] => {
        const seen = new Set<string>();
        return reminders.filter(reminder => {
            const key = getReminderKey(reminder);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [reminders]);

    return {
        validationErrors,
        hasDuplicates,
        getUniqueReminders,
        findDuplicateIndexes: () => findDuplicateReminders(reminders)
    };
};

// Helper to create a unique key for a reminder
export const getReminderKey = (reminder: DiaryReminderDto): string => {
    return `${reminder.offsetMinutes}|${reminder.channel}|${reminder.targetUserId ?? 'creator'}|${reminder.isEnabled}`;
};